import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import SEOHead from '../components/SEOHead';
import { getAllEvents, getImageUrl } from '../utils/contentful';
import { MEDIA_ASSETS } from '../utils/mediaAssets';

const EventsPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scrollToNewsletter = () => {
    const element = document.getElementById('newsletter-section');
    const elementPosition = element.offsetTop;
    const headerHeight = 80; // Approximate header height
    const offsetPosition = elementPosition - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsData = await getAllEvents();
        setEvents(eventsData);
        setLoading(false);
      } catch (err) {
        setError('Failed to load events');
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    // Check if we have state passed from navigation (e.g., from event details page)
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    } else {
      setActiveTab('upcoming'); // Default to upcoming
    }
  }, [location]);

  const currentDate = new Date();
  
  const upcomingEvents = events.filter(event => {
    const eventDate = new Date(event.fields.date);
    return eventDate > currentDate;
  });

  const pastEvents = events.filter(event => {
    const eventDate = new Date(event.fields.date);
    return eventDate <= currentDate;
  });

  // Calculate statistics
  const totalParticipants = pastEvents.reduce((total, event) => {
    return total + (event.fields.participants || 0);
  }, 0);

  const totalWasteCollected = pastEvents.reduce((total, event) => {
    return total + (event.fields.collectedWaste || 0);
  }, 0);

  const renderEventCard = (event) => {
    const { fields } = event;
    const eventDate = new Date(fields.date);
    const day = eventDate.toLocaleDateString('bg-BG', { day: 'numeric' });
    const month = eventDate.toLocaleDateString('bg-BG', { month: 'long' });
    const year = eventDate.toLocaleDateString('bg-BG', { year: 'numeric' });
    const formattedTime = eventDate.toLocaleTimeString('bg-BG', {
      hour: '2-digit',
      minute: '2-digit'
    });

    // Get the main image if it exists
    const mainImage = fields.mainImage 
      ? getImageUrl(fields.mainImage)
      : null;

    const isPastEvent = eventDate <= currentDate;

    return (
      <div key={event.sys.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-105">
        <Link to={`/events/${event.sys.id}`}>
          <div className="h-48 bg-gray-200 relative cursor-pointer overflow-hidden">
            <div className={`absolute top-4 right-4 z-10 ${isPastEvent ? 'bg-gray-600' : 'bg-green-600'} text-white px-3 py-2 rounded-lg shadow-lg text-center`}>
              <div className="text-lg font-bold">{day}</div>
              <div className="text-xs uppercase tracking-wide">{month}</div>
              <div className="text-xs">{year}</div>
            </div>
            {mainImage ? (
              <img 
                src={mainImage} 
                alt={fields.title}
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-100 to-green-200">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-green-600 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-green-600 font-medium">Няма снимка</span> 
                </div>
              </div>
            )}
          </div>
        </Link>
        <div className="p-6">
          <Link to={`/events/${event.sys.id}`}>
            <h3 className="text-xl font-bold text-green-800 mb-3 hover:text-green-600 cursor-pointer transition-colors duration-200 line-clamp-2">{fields.title}</h3>
          </Link>
          
          <div className="space-y-3 mb-4">
            <div className="flex items-center text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">{formattedTime}</span>
            </div>
            
            {fields.locationText && (
              <div className="flex items-center text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="font-medium truncate">{fields.locationText}</span>
              </div>
            )}
          </div>
          
          <div className="mt-4">
            <Link 
              to={`/events/${event.sys.id}`} 
              className="w-full inline-flex items-center justify-center bg-green-700 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
            >
              {isPastEvent ? 'Вижте резултатите' : 'Повече информация'}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    );
  };



  return (
    <MainLayout newsletterId="newsletter-section">
      <SEOHead 
        title="Събития - Refresh Bulgaria"
        description="Разгледайте предстоящите и минали екологични събития на Refresh Bulgaria. Присъединете се към доброволчески акции за почистване в цяла България."
        keywords="екологични събития България, доброволчески акции, почистване събития, volunteer cleanup events"
        url="https://refreshbulgaria.com/events"
      />
      <div className="max-w-screen-xl mx-auto px-4">
        <main className="mt-4">
          {/* Hero section */}
          <section className="mb-8">
            <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden mb-6">
              <img 
                src={MEDIA_ASSETS.eventsHeroImage} 
                alt="Еко събития и доброволчески акции за почистване от Refresh Bulgaria" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="text-center text-white p-6">
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                    Нашите <span className="text-green-300">събития</span>
                  </h1>
                  <p className="text-base md:text-xl font-semibold opacity-90 max-w-2xl">
                    Заедно за по-чиста България
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Toggle Section */}
          <div className="mb-8">
            <div className="flex justify-center">
              <div className="bg-white rounded-lg shadow-md overflow-hidden border w-full max-w-2xl">
                <button 
                  onClick={() => setActiveTab('upcoming')}
                  className={`w-1/2 py-3 md:py-4 px-4 md:px-8 font-semibold text-sm md:text-base transition-all duration-300 ${
                    activeTab === 'upcoming' 
                      ? 'text-white bg-green-700 shadow-lg' 
                      : 'text-green-700 bg-white hover:bg-green-50'
                  }`}
                >
                  <span className="hidden sm:inline">Предстоящи събития ({upcomingEvents.length})</span>
                  <span className="sm:hidden">Предстоящи ({upcomingEvents.length})</span>
                </button>
                <button 
                  onClick={() => setActiveTab('past')}
                  className={`w-1/2 py-3 md:py-4 px-4 md:px-8 font-semibold text-sm md:text-base transition-all duration-300 ${
                    activeTab === 'past' 
                      ? 'text-white bg-green-700 shadow-lg' 
                      : 'text-green-700 bg-white hover:bg-green-50'
                    }`}
                >
                  <span className="hidden sm:inline">Минали събития ({pastEvents.length})</span>
                  <span className="sm:hidden">Минали ({pastEvents.length})</span>
                </button>
              </div>
            </div>
          </div>

          {/* Events Grid */}
          <section className="mb-8">
            {loading && (
              <div className="flex justify-center items-center py-16">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto mb-4"></div>
                  <p className="text-gray-600">Зареждане на събитията...</p>
                </div>
              </div>
            )}

            {error && (
              <div className="flex justify-center items-center py-16">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-600">{error}</p>
                </div>
              </div>
            )}

            {!loading && !error && activeTab === 'upcoming' && upcomingEvents.length === 0 && (
              <div className="text-center py-16 bg-white rounded-lg shadow-md">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">Няма предстоящи събития</h3>
                <p className="text-gray-500 mb-6">Нови събития ще бъдат обявени скоро. Абонирайте се за нашия бюлетин, за да не пропускате новини!</p>
                <button 
                  onClick={scrollToNewsletter} 
                  className="inline-flex items-center bg-green-700 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Абонирайте се за новини
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            )}

            {!loading && !error && activeTab === 'past' && pastEvents.length === 0 && (
              <div className="text-center py-16 bg-white rounded-lg shadow-md">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">Няма минали събития</h3>
                <p className="text-gray-500">Първите ни събития ще се появят тук след провеждането им.</p>
              </div>
            )}

            {!loading && !error && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeTab === 'upcoming' 
                  ? upcomingEvents.map(renderEventCard)
                  : pastEvents.map(renderEventCard)
                }
              </div>
            )}
          </section>

          {/* Statistics Section - only show if we have past events */}
          {pastEvents.length > 0 && (
            <section className="mb-8">
              <div className="bg-gradient-to-r from-green-700 to-green-600 rounded-lg p-6 text-white">
                <h2 className="text-2xl font-bold text-center mb-6">Нашите постижения</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="bg-white bg-opacity-20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="text-3xl font-bold mb-1">{pastEvents.length}</div>
                    <div className="text-green-100">Проведени събития</div>
                  </div>
                  
                  {totalParticipants > 0 && (
                    <div className="text-center">
                      <div className="bg-white bg-opacity-20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <div className="text-3xl font-bold mb-1">{totalParticipants}</div>
                      <div className="text-green-100">Доброволци</div>
                    </div>
                  )}
                  
                  {totalWasteCollected > 0 && (
                    <div className="text-center">
                      <div className="bg-white bg-opacity-20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </div>
                      <div className="text-3xl font-bold mb-1">{totalWasteCollected}</div>
                      <div className="text-green-100">Килограма отпадъци</div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

        </main>
      </div>
    </MainLayout>
  );
};

export default EventsPage; 